import type { Difficulty, RecipeListQuery, RecipeSort } from '@recipes/contracts';
import { recipeSortSchema, difficultySchema } from '@recipes/contracts';

export interface RecipeFilterState {
  q: string;
  categoryIds: string[];
  tags: string[];
  cuisine: string;
  mealType: string;
  difficulty: Difficulty | '';
  minRating: number | null;
  favorite: boolean;
  baseRecipe: boolean;
  maxTotalMinutes: number | null;
  dietary: string[];
  excludeAllergens: string[];
  availableIngredients: string[];
  sort: RecipeSort;
  order: 'asc' | 'desc' | '';
  trashed: boolean;
  includeArchived: boolean;
}

export const DEFAULT_FILTERS: RecipeFilterState = {
  q: '',
  categoryIds: [],
  tags: [],
  cuisine: '',
  mealType: '',
  difficulty: '',
  minRating: null,
  favorite: false,
  baseRecipe: false,
  maxTotalMinutes: null,
  dietary: [],
  excludeAllergens: [],
  availableIngredients: [],
  sort: 'recentlyUpdated',
  order: '',
  trashed: false,
  includeArchived: false,
};

const csv = (v: string | null) =>
  v
    ? v
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

export function filtersFromSearchParams(params: URLSearchParams): RecipeFilterState {
  const sort = recipeSortSchema.safeParse(params.get('sort'));
  const difficulty = difficultySchema.safeParse(params.get('difficulty'));
  const minRating = Number(params.get('minRating'));
  const maxTotal = Number(params.get('maxTotalMinutes'));
  const order = params.get('order');
  return {
    q: params.get('q') ?? '',
    categoryIds: csv(params.get('categoryIds')),
    tags: csv(params.get('tags')),
    cuisine: params.get('cuisine') ?? '',
    mealType: params.get('mealType') ?? '',
    difficulty: difficulty.success ? difficulty.data : '',
    minRating: minRating > 0 ? minRating : null,
    favorite: params.get('favorite') === '1',
    baseRecipe: params.get('base') === '1',
    maxTotalMinutes: maxTotal > 0 ? maxTotal : null,
    dietary: csv(params.get('dietary')),
    excludeAllergens: csv(params.get('excludeAllergens')),
    availableIngredients: csv(params.get('availableIngredients')),
    sort: sort.success ? sort.data : DEFAULT_FILTERS.sort,
    order: order === 'asc' || order === 'desc' ? order : '',
    trashed: params.get('trashed') === '1',
    includeArchived: params.get('includeArchived') === '1',
  };
}

export function filtersToSearchParams(f: RecipeFilterState): URLSearchParams {
  const params = new URLSearchParams();
  if (f.q) params.set('q', f.q);
  if (f.categoryIds.length) params.set('categoryIds', f.categoryIds.join(','));
  if (f.tags.length) params.set('tags', f.tags.join(','));
  if (f.cuisine) params.set('cuisine', f.cuisine);
  if (f.mealType) params.set('mealType', f.mealType);
  if (f.difficulty) params.set('difficulty', f.difficulty);
  if (f.minRating) params.set('minRating', String(f.minRating));
  if (f.favorite) params.set('favorite', '1');
  if (f.baseRecipe) params.set('base', '1');
  if (f.maxTotalMinutes) params.set('maxTotalMinutes', String(f.maxTotalMinutes));
  if (f.dietary.length) params.set('dietary', f.dietary.join(','));
  if (f.excludeAllergens.length) params.set('excludeAllergens', f.excludeAllergens.join(','));
  if (f.availableIngredients.length)
    params.set('availableIngredients', f.availableIngredients.join(','));
  if (f.sort !== DEFAULT_FILTERS.sort) params.set('sort', f.sort);
  if (f.order) params.set('order', f.order);
  if (f.trashed) params.set('trashed', '1');
  if (f.includeArchived) params.set('includeArchived', '1');
  return params;
}

/** Query object for api.recipes.list; booleans are only sent when true (z.coerce.boolean). */
export function filtersToQuery(f: RecipeFilterState): Partial<RecipeListQuery> {
  const query: Partial<RecipeListQuery> = { sort: f.sort, limit: 24 };
  if (f.q) query.q = f.q;
  if (f.categoryIds.length) query.categoryIds = f.categoryIds;
  if (f.tags.length) query.tags = f.tags;
  if (f.cuisine) query.cuisine = f.cuisine;
  if (f.mealType) query.mealType = f.mealType;
  if (f.difficulty) query.difficulty = f.difficulty;
  if (f.minRating) query.minRating = f.minRating;
  if (f.favorite) query.favorite = true;
  if (f.baseRecipe) query.baseRecipe = true;
  if (f.maxTotalMinutes) query.maxTotalMinutes = f.maxTotalMinutes;
  if (f.dietary.length) query.dietary = f.dietary as RecipeListQuery['dietary'];
  if (f.excludeAllergens.length)
    query.excludeAllergens = f.excludeAllergens as RecipeListQuery['excludeAllergens'];
  if (f.availableIngredients.length) query.availableIngredients = f.availableIngredients;
  if (f.order) query.order = f.order;
  if (f.trashed) query.trashed = true;
  if (f.includeArchived) query.includeArchived = true;
  if (f.q && f.sort === DEFAULT_FILTERS.sort) query.sort = 'relevance';
  return query;
}

/** Number of active filters, for the filter button badge (search, sort and trash excluded). */
export function activeFilterCount(f: RecipeFilterState): number {
  let n = 0;
  if (f.categoryIds.length) n += 1;
  if (f.tags.length) n += 1;
  if (f.cuisine) n += 1;
  if (f.mealType) n += 1;
  if (f.difficulty) n += 1;
  if (f.minRating) n += 1;
  if (f.favorite) n += 1;
  if (f.baseRecipe) n += 1;
  if (f.maxTotalMinutes) n += 1;
  if (f.dietary.length) n += 1;
  if (f.excludeAllergens.length) n += 1;
  if (f.availableIngredients.length) n += 1;
  if (f.includeArchived) n += 1;
  return n;
}
